import { ChangeEvent, RefObject, useRef, useState } from "react";
import { IconButton, Typography } from "@material-ui/core";
import { Delete } from "@material-ui/icons";
import { CurrentBundleContainer } from "../../../MobileAppModal.style";
import {
  BundleLink,
  ButtonCustom,
  ImportFileUpload,
} from "../../../../shared/Style/Style";
import { mobileAppModel } from "../../state";
import { CurrentBundleType } from "../../types";

// The localization archive and its manifest are one unit: the manifest carries the hashes and sizes
// of a specific archive, so a manifest left over from a previous archive breaks the client's
// download. Every operation here is therefore a single call carrying both files — attaching,
// replacing and removing alike — and the backend refuses anything that would leave a row holding
// one of the two. The attach controls stay on screen once the pair is complete, so a new version
// never has to go through a delete first.

type LocalizationPairProps = {
  modelId: string;
  currentBundle?: CurrentBundleType;
  resourcesLabel: string;
  resourcesManifestLabel: string;
  isLoading: boolean;
};

type PickedFiles = {
  resources?: File;
  resourcesManifest?: File;
};

export const LocalizationPair = ({
  modelId,
  currentBundle,
  resourcesLabel,
  resourcesManifestLabel,
  isLoading,
}: LocalizationPairProps) => {
  const resourcesInput = useRef<HTMLInputElement | null>(null);
  const resourcesManifestInput = useRef<HTMLInputElement | null>(null);
  const [picked, setPicked] = useState<PickedFiles>({});

  const bundleId = currentBundle?.id;
  const uploadedResources = currentBundle?.resources;
  const uploadedResourcesManifest = currentBundle?.resourcesManifest;

  const hasAnyUploaded = !!uploadedResources || !!uploadedResourcesManifest;
  const isPairComplete = !!uploadedResources && !!uploadedResourcesManifest;
  const canSave = !!picked.resources && !!picked.resourcesManifest;

  // With one of the two picked, canSave stays false and the save button would otherwise sit
  // disabled with nothing on screen saying what is missing — and when the uploaded pair is already
  // complete there is no other message on screen at all. The pending choice is the actionable one,
  // so it wins over the note about what is currently stored.
  const hint =
    !!picked.resources !== !!picked.resourcesManifest
      ? "Прикріпіть і другий файл — архів і маніфест заливаються разом."
      : hasAnyUploaded && !isPairComplete
      ? "Файли локалізації неповні. Архів і маніфест завантажуються разом — прикріпіть обидва файли, вони замінять наявні."
      : null;

  const pick =
    (field: keyof PickedFiles) =>
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      const file = target.files?.[0];

      // Clear the input so re-picking the same file fires onChange again.
      target.value = "";

      if (!file) return;

      setPicked((current) => ({ ...current, [field]: file }));
    };

  const save = async () => {
    const { resources, resourcesManifest } = picked;

    if (!resources || !resourcesManifest || !bundleId) return;

    const saved = await mobileAppModel.updateModalFile({
      modelId,
      id: bundleId,
      resources,
      resourcesManifest,
    });

    // Keep the selection on failure: the admin has already browsed for both files, and the error
    // is usually transient.
    if (saved) setPicked({});
  };

  const removePair = async () => {
    if (!bundleId) return;

    await mobileAppModel.deleteBundleFile(modelId, bundleId);
  };

  const renderAttachButton = (
    field: keyof PickedFiles,
    label: string,
    inputRef: RefObject<HTMLInputElement>,
  ) => (
    <CurrentBundleContainer>
      <span>{picked[field]?.name || label}</span>

      <ButtonCustom
        disabled={isLoading || !bundleId}
        onClick={() => inputRef.current?.click()}
      >
        <span>{picked[field] ? "Замінити" : "Прикріпити"}</span>
        <ImportFileUpload type="file" ref={inputRef} onChange={pick(field)} />
      </ButtonCustom>
    </CurrentBundleContainer>
  );

  return (
    <>
      {isPairComplete && (
        <>
          <CurrentBundleContainer>
            <BundleLink href={uploadedResources}>{resourcesLabel}</BundleLink>

            <IconButton disabled={isLoading || !bundleId} onClick={removePair}>
              <Delete color="secondary" />
            </IconButton>
          </CurrentBundleContainer>

          <CurrentBundleContainer>
            <BundleLink href={uploadedResourcesManifest}>
              {resourcesManifestLabel}
            </BundleLink>
          </CurrentBundleContainer>
        </>
      )}

      {hasAnyUploaded && !isPairComplete && (
        <CurrentBundleContainer>
          <BundleLink href={uploadedResources || uploadedResourcesManifest}>
            {uploadedResources ? resourcesLabel : resourcesManifestLabel}
          </BundleLink>

          <IconButton disabled={isLoading || !bundleId} onClick={removePair}>
            <Delete color="secondary" />
          </IconButton>
        </CurrentBundleContainer>
      )}

      {!!hint && (
        <Typography
          variant="body2"
          color="error"
          style={{ maxWidth: "480px", marginTop: "16px" }}
        >
          {hint}
        </Typography>
      )}

      {renderAttachButton("resources", resourcesLabel, resourcesInput)}
      {renderAttachButton(
        "resourcesManifest",
        resourcesManifestLabel,
        resourcesManifestInput,
      )}

      <CurrentBundleContainer>
        <ButtonCustom
          disabled={isLoading || !bundleId || !canSave}
          onClick={save}
        >
          <span>
            {hasAnyUploaded ? "Замінити локалізацію" : "Зберегти локалізацію"}
          </span>
        </ButtonCustom>
      </CurrentBundleContainer>
    </>
  );
};
